import { Check, Code2, Copy, Download } from "lucide-react";
import PropTypes from "prop-types";
import { useRef, useState } from "react";

/**
 * TemplateCodeEditor - Code editor component for template full code
 * Features:
 * - Syntax highlighting via textarea
 * - Copy to clipboard with visual feedback
 * - Optional download as .js file
 * - Character counter
 */
export default function TemplateCodeEditor({
	value,
	onChange,
	templateName,
	onPreview,
	codeType = "react",
	onCodeTypeChange,
}) {
	const [copied, setCopied] = useState(false);
	const [isPreviewLoading, setIsPreviewLoading] = useState(false);
	const textareaRef = useRef(null);

	const handlePreview = () => {
		if (!onPreview) return;
		setIsPreviewLoading(true);
		onPreview(value);
		setTimeout(() => setIsPreviewLoading(false), 800);
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(value || "");
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	const handleDownload = () => {
		const isHtml = codeType === "html";
		const blob = new Blob([value || ""], {
			type: isHtml ? "text/html" : "text/javascript",
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${templateName || "template"}.${isHtml ? "html" : "js"}`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const charCount = (value || "").length;

	return (
		<div className="space-y-2">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-1">
				<div className="flex flex-wrap items-center gap-3">
					<div className="flex items-center gap-2">
						<Code2 className="w-4 h-4 text-cyan-400" />
						<label
							htmlFor="template-code-textarea"
							className="text-sm font-semibold text-white"
						>
							Código do Template
						</label>
					</div>

					<select
						value={codeType}
						onChange={(e) => onCodeTypeChange?.(e.target.value)}
						className="bg-[#0f172a] border border-slate-700 text-xs text-slate-300 rounded px-2.5 py-1 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all font-medium"
					>
						<option value="react">React (JSX)</option>
						<option value="html">HTML Estático</option>
					</select>
				</div>
				<div className="flex items-center gap-2 self-end sm:self-auto">
					{/* Character counter */}
					{charCount > 0 && (
						<span className="text-[10px] text-slate-500 font-mono">
							{charCount.toLocaleString()} caracteres
						</span>
					)}

					{/* Download button */}
					{value && (
						<button
							type="button"
							onClick={handleDownload}
							className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-all"
							title={`Baixar como arquivo .${codeType === "html" ? "html" : "js"}`}
						>
							<Download className="w-3 h-3" />
							<span className="hidden sm:inline">Download</span>
						</button>
					)}

					{/* Preview button */}
					<button
						type="button"
						onClick={handlePreview}
						disabled={!value || isPreviewLoading}
						className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded transition-all bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 border border-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed`}
						title="Renderizar preview do código"
					>
						{isPreviewLoading ? (
							<div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
						) : (
							<Code2 className="w-3 h-3" />
						)}
						Visualizar Preview
					</button>

					{/* Copy button */}
					<button
						type="button"
						onClick={handleCopy}
						disabled={!value}
						className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded transition-all ${
							copied
								? "bg-green-600/20 text-green-400 border border-green-500/30"
								: "bg-cyan-600/10 text-cyan-400 hover:bg-cyan-600/20 border border-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
						}`}
						title="Copiar código"
					>
						{copied ? (
							<>
								<Check className="w-3 h-3" />
								Copiado!
							</>
						) : (
							<>
								<Copy className="w-3 h-3" />
								Copiar
							</>
						)}
					</button>
				</div>
			</div>

			{/* Description */}
			<p className="text-[10px] text-slate-500">
				{codeType === "html" ? (
					<>
						Cole aqui o código HTML completo. Você pode usar classes do{" "}
						<code className="text-cyan-400">Tailwind CSS</code> e ícones do{" "}
						<code className="text-cyan-400">Lucide</code> através de{" "}
						<code className="text-cyan-400">
							&lt;i data-lucide="nome"&gt;&lt;/i&gt;
						</code>
						.
					</>
				) : (
					<>
						Cole aqui o código React completo da proposta (formato similar ao{" "}
						<code className="text-cyan-400">/base.js</code>). Este código será
						compilado em tempo real.
					</>
				)}
			</p>

			{/* Code Editor */}
			<div className="relative">
				<textarea
					ref={textareaRef}
					value={value || ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={
						codeType === "html"
							? `<!-- Exemplo HTML Estático -->
<div class="max-w-4xl mx-auto py-12 px-6">
  <div class="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 backdrop-blur-xl">
    <div class="flex items-center gap-3 mb-6">
      <i data-lucide="star" class="w-6 h-6 text-cyan-400"></i>
      <h1 class="text-3xl font-extrabold text-white tracking-tight">Proposta Comercial</h1>
    </div>
    
    <p class="text-slate-300 leading-relaxed">
      Olá {{client_name}}, preparamos esta proposta com muito cuidado para a empresa {{client_company}}.
    </p>
  </div>
</div>`
							: `// Cole aqui o código completo do template
import React from 'react';

const App = () => {
  // Seu código aqui...
  return (
    <div>
      {/* Conteúdo */}
    </div>
  );
};

export default App;`
					}
					id="template-code-textarea"
					className="w-full h-96 bg-[#0B0F19] border border-slate-700 rounded-lg p-4 text-slate-300 font-mono text-xs leading-relaxed resize-y focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all"
					spellCheck="false"
				/>

				{/* Visual feedback overlay when copied */}
				{copied && (
					<div className="absolute inset-0 bg-green-500/5 border-2 border-green-500/30 rounded-lg pointer-events-none animate-pulse" />
				)}
			</div>

			{/* Help text */}
			<div className="flex items-start gap-2 p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg">
				<div className="text-cyan-400 mt-0.5">
					<Code2 className="w-3 h-3" />
				</div>
				<div className="flex-1">
					<p className="text-[11px] text-slate-400 leading-relaxed">
						<strong className="text-white">Dica:</strong> Use as variáveis entre
						chaves duplas como{" "}
						<code className="text-cyan-400">{"{{client_name}}"}</code> ou{" "}
						<code className="text-cyan-400">{"{{client_company}}"}</code> para
						substituí-las automaticamente com os valores reais do cliente no
						preview e na visualização da proposta.
					</p>
				</div>
			</div>
		</div>
	);
}

TemplateCodeEditor.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	templateName: PropTypes.string,
	onPreview: PropTypes.func,
	codeType: PropTypes.string,
	onCodeTypeChange: PropTypes.func,
};
